import React, { useState } from 'react';

import { TFLoadingOverlay } from '@tf/ui';
import { S, splitObjectId } from '@tf/utils';

import { useOverviewAccount } from '@/core/api/overview';
import type {
	CaseRequestStatusChanged,
	CaseStatusChanged,
	OSReviewStateChangedEvent,
	ProfileAssignedManagersChanged,
	ProfileConnectionStatusChanged,
	ProfileEvent,
	ProfileOnboardingDateChangedEvent,
	ProfileStatusChangedEvent,
} from '@/core/api/profileEvents';
import { useAccountEventsLog } from '@/core/api/profileEvents';
import { useAccountParams } from '@/core/hooks';
import { getLabel } from '@/core/utils';
import { BaseEventLog } from '@/components/shared';

const eventMessages: Record<string, (e: any) => string> = {
	profile_created: (): string => 'created a new account',

	profile_status_changed: (e: ProfileStatusChangedEvent) => {
		switch (e.meta.profile_status) {
			case 'CLOSED':
				return 'closed the account';
			case 'ACTIVE':
				return 'activated the account';
			case 'REJECTED':
				return 'rejected the application';
			case 'SUSPENDED':
				return 'suspended the account';
			case 'ARCHIVED':
				return 'has archived the account';
			default:
				return `updated the account status to ${getLabel(e.meta.profile_status, {
					kind: 'accountStatus',
				})}`;
		}
	},

	profile_onboarding_date_changed: (e: ProfileOnboardingDateChangedEvent) => {
		return 'changed the onboarding date';
	},

	profile_assigned_managers_changed: (e: ProfileAssignedManagersChanged) => {
		if (e.meta.new_managers?.length) {
			return 'assigned a new manager to the account';
		}
		return 'removed the manager from the account';
	},

	profile_cra_calculated: () => 'calculated the customer risk rating',

	profile_kyc_date_reached: () => 'KYC review is overdue',

	os_review_state_changed: (e: OSReviewStateChangedEvent) => {
		switch (e.meta.os_review_state_value) {
			case 'READY_TO_REVIEW':
				return 'sent the account to compliance review';
			case 'UPDATE_REQUIRED':
				return 'requested more information to complete the review';
			case 'VERIFIED':
				return 'verified the account';
			default:
				return `updated the review status to ${getLabel(e.meta.os_review_state_value, {
					kind: 'reviewState',
				})}`;
		}
	},

	profile_connection_status_changed: (e: ProfileConnectionStatusChanged) => {
		const [profile_a, profile_b] = e.nestedRelation;

		const accountAKind = S.prettify(profile_a.object.kind);
		const accountBKind = S.prettify(profile_b.object.kind);

		switch (e.meta.new_status) {
			case 'ACTIVE':
				return `create new connection between ${accountAKind} and ${accountBKind}`;
			case 'REMOVED':
				return `delete connection between ${accountAKind} and ${accountBKind}`;

			default:
				return 'Unknown connection status';
		}
	},

	os_review_comment_created: () => 'added a new comment',

	// Case events
	case_status_changed: (e: CaseStatusChanged) => {
		switch (e.meta.case_status) {
			case 'ACTIVE':
				return 'opened a new case';
			case 'CLOSED':
				return 'closed the case';
			default:
				// fallback, in case of new status added
				return `changed case status to ${e.meta.case_status}`;
		}
	},

	case_request_status_changed: (e: CaseRequestStatusChanged) => {
		switch (e.meta.case_request_status) {
			case 'VIEWED':
				return 'viewed the case';
			case 'REVOKED':
				return 'revoked the case request';
			case 'UPDATED':
				return 'updated the case';
			case 'SENT':
				return 'sent the case request';
			default:
				// fallback, in case of new status added
				return `changed case status to ${e.meta.case_request_status}`;
		}
	},
};

const availableFilters = [
	// TODO: need better texts for options as previous were done as actor messages; plus, now we have more complex events
	//	Temporarily disabling this filter
	// {
	// 	name: 'event',
	// 	label: 'Events',
	// 	type: 'select',
	// 	value: Object.entries(ACCOUNT_STATUS_MESSAGES).map(([key, value]) => ({
	// 		label: value,
	// 		value: key,
	// 	})),
	// },
	{
		name: 'date',
		label: 'Date range',
		type: 'date',
		value: [null, null],
	},
];

const useFilteredEvents = (events?: ProfileEvent[]) => {
	const [searchValue, setSearchValue] = useState('');
	const [filters, setFilters] = useState<{ id: string; value: unknown }[]>([]);

	if (!events) {
		return;
	}

	const filteredEvents = events.filter((event) => {
		if (!filters.length && !searchValue) return true;

		const eventFilters = filters.find((f) => f.id === 'event');
		const dateFilters = filters.find((f) => f.id === 'date');

		let matchesEventKind = true;
		let matchesDateRange = true;
		let matchesSearch = true;

		if (eventFilters) {
			matchesEventKind = eventFilters.value === event.kind;
		}

		if (dateFilters && Array.isArray(dateFilters.value)) {
			const [startDate, endDate] = dateFilters.value.map((dateStr) => new Date(dateStr));
			const eventDate = new Date(event.meta['@createdAt']);
			eventDate.setHours(0, 0, 0, 0);
			matchesDateRange = eventDate >= startDate && eventDate <= endDate;
		}

		if (searchValue) {
			const eventLabel = eventMessages[event.kind]?.(event).toLowerCase() ?? '';
			matchesSearch = eventLabel?.includes(searchValue.toLowerCase());
		}

		return matchesEventKind && matchesDateRange && matchesSearch;
	});

	return { filteredEvents, searchValue, setSearchValue, filters, setFilters };
};

export const AccountEventLog = () => {
	const { accountId } = useAccountParams();

	const { data: events, isPending: isEventsLogPending } = useAccountEventsLog(accountId);
	const { data } = useOverviewAccount(accountId);
	const listUsers = data?.listUsers;

	const mainReviewEvents = events?.filter((e) => {
		if (e.kind === 'os_review_state_changed') {
			// Show events only for the main review
			return e.nestedRelation[0].object.meta.review_relation_kind === 'APPLICANT';
		}
		return true;
	});

	const sortedEvents = mainReviewEvents?.slice().sort((a, b) => {
		const createdAtA = new Date(b.meta['@createdAt']).getTime();
		const createdAtB = new Date(a.meta['@createdAt']).getTime();
		if (a.kind !== 'profile_created' && b.kind === 'profile_created') {
			return -1;
		}
		return createdAtA - createdAtB;
	});
	const eventsFilters = useFilteredEvents(sortedEvents);

	if (isEventsLogPending || !eventsFilters || !listUsers) {
		return <TFLoadingOverlay h={300} size={24} />;
	}

	const { setSearchValue, filteredEvents, setFilters, filters, searchValue } = eventsFilters;

	return (
		<BaseEventLog
			searchValue={searchValue}
			setSearchValue={setSearchValue}
			filters={filters}
			setFilters={setFilters}
			events={filteredEvents}
			listUsers={listUsers}
			// TODO: There is type error + date picker is not working at all
			// availableFilters={availableFilters as FilterItem[]}
			messagesMap={eventMessages}
		/>
	);
};
